import React from 'react'
import {Box, Typography} from '@mui/material'
import {ColumnDef} from 'src/components-new/data/DataTable'
import InitialsAvatar from 'src/components-new/primitives/InitialsAvatar'
import {familyTypes} from 'src/constants/constants'

export interface CaregiverRecord {
  _id: string
  title?: string
  lastName?: string
  firstName?: string
  email?: string
  phoneNumber?: string
  cellPhoneNumber?: string
  caregiverType?: string
  [key: string]: any
}

// Stesso dominio di FamilyCardSection.tsx/ProfileNew.tsx — caregiverType è lo stesso
// campo/enum sia per la family card del profilo che per i caregiver/tutori del paziente
// (vedi anche il fixture mock psp-backend/patients/{patientId}/caregivers/get.js). Stessa
// mappatura usata nel dettaglio (CaregiverDetailDialogNew.tsx).
export function getCaregiverTypeLabel(caregiverType?: string): string | undefined {
  if (!caregiverType) return undefined
  return (familyTypes as Record<string, string>)[caregiverType] ?? caregiverType
}

function getCaregiverName(row: CaregiverRecord): string {
  return [row.firstName, row.lastName].filter(Boolean).join(' ') || '—'
}

function getCaregiverInitials(row: CaregiverRecord): string {
  return `${row.firstName?.[0] ?? ''}${row.lastName?.[0] ?? ''}`
}

// Titolo/cognome/nome/tipo confluiscono sempre nella cella identità "Caregiver" (avatar
// con iniziali colorate, come InitialsAvatar in PatientsListNew, + nome in grassetto +
// tipo come sottotitolo) e non vengono mai renderizzati come colonne separate, anche
// quando la PSP li configura esplicitamente in caregiversConfiguration.columnOptions —
// altrimenti una PSP che lista "title" tra le colonne finiva con una tabella piatta
// TITOLO/COGNOME/NOME invece dello stile coerente con la lista pazienti, e "Tipo"
// comparirebbe due volte (colonna + sottotitolo). Il configuratore di backoffice
// (patientCaregiversColumnOptions in PspConfiguratorModal/CONSTANTS.ts) non offre comunque
// "title"/"lastName" tra i campi selezionabili: questo filtro resta come difesa a costo
// zero. "email" non è tra questi id: sia nel default sia nel path PSP-configurato resta una
// colonna piatta a sé.
const CAREGIVER_IDENTITY_IDS = new Set(['title', 'firstName', 'lastName', 'caregiverType'])

// Stesso pattern della colonna "Paziente" in src/pages-new/patients/PatientsListNew.tsx —
// avatar con iniziali + nome in grassetto, cosi' la tabella dei caregiver/tutori è
// visivamente coerente con quella dei pazienti invece di un elenco di colonne piatte come
// nella versione legacy. Il tipo (ruolo del delegato) compare come sottotitolo sotto il
// nome SOLO se configurato — showType riflette esattamente se "caregiverType" è tra i
// campi PSP-configurati (o se non c'è config, dove per definizione tutto è visibile —
// vedi DEFAULT_COLUMNS sotto), stesso principio già applicato a "firstName" per l'intera
// cella identità.
function makeIdentityColumn(showType: boolean): ColumnDef<CaregiverRecord> {
  return {
    key: 'caregiver',
    header: 'Caregiver',
    mobileCardHeader: true,
    render: row => (
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <InitialsAvatar initials={getCaregiverInitials(row)} />
        <Box sx={{minWidth: 0}}>
          <Typography variant="body2" fontWeight={600}>
            {getCaregiverName(row)}
          </Typography>
          {showType && (
            <Typography variant="caption" sx={{color: 'text.secondary', display: 'block'}}>
              {getCaregiverTypeLabel(row.caregiverType) ?? '—'}
            </Typography>
          )}
        </Box>
      </Box>
    )
  }
}

// Renderer unico condiviso da DEFAULT_COLUMNS e buildColumnsFromPsp, cosi' i due path non
// possono più divergere (in precedenza l'email perdeva l'overflowWrap nel path PSP). "id"
// supporta path puntati (es. "address.city"), come in PatientsListNew.tsx. Telefono e
// cellulare restano colonne distinte, senza fallback incrociato tra i due campi (come in
// CaregiverDetailDialogNew) — il ramo generico in fondo basta per entrambi. "caregiverType"
// non passa mai da qui: è sempre assorbito nella cella identità (vedi
// CAREGIVER_IDENTITY_IDS/makeIdentityColumn sopra), mai una colonna piatta.
function makeFlatColumn(id: string, header: string): ColumnDef<CaregiverRecord> {
  if (id === 'email') {
    return {
      key: id,
      header,
      render: row => <Typography variant="body2" sx={{overflowWrap: 'anywhere'}}>{row.email ?? '—'}</Typography>
    }
  }
  return {
    key: id,
    header,
    render: row => {
      const val = id.split('.').reduce((obj: any, k) => obj?.[k], row)
      return <Typography variant="body2">{val ?? '—'}</Typography>
    }
  }
}

// Nessuna config PSP = semanticamente "tutti i campi visibili" — quindi il tipo compare
// come sottotitolo sotto il nome (showType=true), stesso trattamento che avrebbe con
// buildColumnsFromPsp sotto se la PSP configurasse esplicitamente tutti e 5 i campi.
export const DEFAULT_COLUMNS: ColumnDef<CaregiverRecord>[] = [
  makeIdentityColumn(true),
  makeFlatColumn('phoneNumber', 'Telefono'),
  makeFlatColumn('cellPhoneNumber', 'Cellulare'),
  makeFlatColumn('email', 'Email')
]

// Colonne configurate dalla PSP: la cella identità (sopra) compare SOLO se "firstName" è
// tra i campi configurati — rigorosamente PSP-driven, come il programma sotto il nome in
// PatientsListNew, che non compare se la colonna che lo pilota non è configurata. Il tipo,
// se configurato, compare come sottotitolo dentro quella stessa cella (mai una colonna a
// sé) — e quindi, se "firstName" non è configurato, il tipo non compare da nessuna parte
// anche se "caregiverType" lo è: non c'è una cella identità sotto cui mostrarlo. Caso
// limite accettato: il configuratore di backoffice presenta sempre "Nome" come opzione
// naturale tra i 5 campi selezionabili (patientCaregiversColumnOptions in
// ia-web-components/.../PspConfiguratorModal/CONSTANTS.ts), quindi configurare il tipo
// senza il nome è una combinazione di fatto marginale.
export function buildColumnsFromPsp(pspColumns: {id: string; label: string}[]): ColumnDef<CaregiverRecord>[] {
  const showIdentity = pspColumns.some(col => col.id === 'firstName')
  const showType = pspColumns.some(col => col.id === 'caregiverType')
  const extraCols = pspColumns
    .filter(col => !CAREGIVER_IDENTITY_IDS.has(col.id))
    .map(col => makeFlatColumn(col.id, col.label))

  return showIdentity ? [makeIdentityColumn(showType), ...extraCols] : extraCols
}
